import { faCarSide } from '@fortawesome/pro-regular-svg-icons/faCarSide';
import { faPencil } from '@fortawesome/pro-regular-svg-icons/faPencil';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { RecentSearch } from '@propertypal/shared/src/hooks/useRecentSearches';
import { selectProperties } from '@propertypal/shared/src/reducers/properties';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Agent, Property } from '@propertypal/shared/src/types/property';
import { PropertySearchResult } from '@propertypal/shared/src/types/propertySearch';
import platform from '@propertypal/shared/src/utils/platform';
import isHighlightedDevelopment from '@propertypal/shared/src/utils/property/highlightedDevelopment';
import FeaturedSearchAgent from '@propertypal/web-ui/src/agents/FeaturedSearchAgent';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import PropertyPlaceholder from '@propertypal/web-ui/src/property/PropertyPlaceholder';
import SearchPropertyBox from '@propertypal/web-ui/src/property-box/SearchPropertyBox';
import AlternativeSearches from '@propertypal/web-ui/src/search/AlternativeSearches';
import SearchTips from '@propertypal/web-ui/src/search/SearchTips';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FunctionComponent, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import AdBox from '../ads/AdBox';
import OpenSearchMap from './OpenSearchMap';
import {
  AdContainer,
  BoxList,
  Container,
  DesktopOnly,
  HandyTools,
  InfoBox,
  InfoHeader,
  List,
  RecentSearchButton,
  StickyBox,
} from './PropertyList.style';
import RecentlyViewed from './RecentlyViewed';

interface Props {
  currency?: string;
  onFavourite?: () => (favourited: boolean, propertyId: number) => Promise<boolean>;
  onHideToggle?: () => (hidden: boolean, propertyId: number) => Promise<boolean>;
  onEmailClick: (property: Property) => void;
  onCallClick: (property: Property) => void;
  onCallAgentClick: (agent: Agent) => void;
  onPropertyClick?: (property: Property) => void;
  locationDetail?: PropertySearchResult['locationDetail'];
  imageLocator: string;
  thumbImageLocator: string;
  onDrawSearchClick: () => void;
  onTravelTimeClick: () => void;
  title: ReactNode;
  upperMiddleAd?: ReactNode;
  lowerMiddleAd?: ReactNode;
  footer: ReactNode;
  recentSearches?: RecentSearch[];
  onRecentSearchClick: (search: RecentSearch) => void;
  onRemoveRecentSearch: (search: RecentSearch) => void;
  photoScroller?: boolean;
  handleAlternativeSearch: (term: string) => void;
  handleSaveSearchClick: () => void;
}

const AD_RIGHT_SIZE = [[300, 250], 'Fluid'];
const AD_RIGHT_BOTTOM_SIZE = [[300, 250], [300, 600], [160, 600], 'Fluid'];

const getPropertyHref = (path: string, currency?: string) => {
  if (!currency || !['EUR', 'USD'].includes(currency)) {
    return path;
  }

  return `${path}?currency=${currency}`;
};

const PropertyList: FunctionComponent<Props> = (props) => {
  const router = useRouter();
  const properties = useSelector(selectProperties);
  const theme = useTheme();

  const trackRefineLink = (type: string) => () => {
    trackGaEvent('refine_search_click', { type });
  };

  const handlePropertyClick = (property: Property) => () => {
    if (props.onPropertyClick) props.onPropertyClick(property);

    if (isHighlightedDevelopment(property)) {
      trackGaEvent('pinned_property_click', {});
    }
  };

  if (!properties.data) {
    return (
      <Container>
        <List>
          {props.title}
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
          <PropertyPlaceholder useWideDisplay />
        </List>

        <HandyTools />
      </Container>
    );
  }

  const topSection = properties.data?.results.slice(0, 4);
  const middleSection = properties.data?.results.slice(4, 8);
  const lowerSection = properties.data?.results.slice(8);
  const hasResults = !!properties.data?.results?.length;

  return (
    <Container>
      <List>
        {platform.select(
          null,
          <OpenSearchMap
            importantPlaces={properties.data.importantPlaces}
            polygons={properties.data?.polygons}
            url={properties.data.url}
            mob
          />,
        )}

        {props.title}

        {properties.data.results && properties.data.results.length === 0 && (
          <>
            <AlternativeSearches searchResult={properties.data} onClick={props.handleAlternativeSearch} />

            <SearchTips searchResult={properties.data} onSaveSearchClick={props.handleSaveSearchClick} />
          </>
        )}

        <BoxList>
          {topSection?.map((property: Property, index: number) => (
            <SearchPropertyBox
              key={`${property.id}${property.featuredListing}${property.type}`}
              href={getPropertyHref(property.path, props.currency)}
              property={property}
              onFavourite={props.onFavourite}
              onHideToggle={props.onHideToggle}
              imageLocator={props.imageLocator}
              locationDetail={props.locationDetail}
              thumbImageLocator={props.thumbImageLocator}
              loading={index < 2 ? 'eager' : 'lazy'}
              useWideDisplay
              useSlider={props.photoScroller}
              onCallClick={props.onCallClick}
              onEmailClick={props.onEmailClick}
              onClick={handlePropertyClick(property)}
            />
          ))}

          {hasResults && props.upperMiddleAd && <li>{props.upperMiddleAd}</li>}

          {middleSection?.map((property: Property) => (
            <SearchPropertyBox
              key={`${property.id}${property.featuredListing}${property.type}`}
              href={getPropertyHref(property.path, props.currency)}
              property={property}
              onFavourite={props.onFavourite}
              onHideToggle={props.onHideToggle}
              imageLocator={props.imageLocator}
              locationDetail={props.locationDetail}
              thumbImageLocator={props.thumbImageLocator}
              loading="lazy"
              useWideDisplay
              useSlider={props.photoScroller}
              onCallClick={props.onCallClick}
              onEmailClick={props.onEmailClick}
              onClick={handlePropertyClick(property)}
            />
          ))}

          {hasResults && props.lowerMiddleAd && <li>{props.lowerMiddleAd}</li>}

          {lowerSection?.map((property: Property) => (
            <SearchPropertyBox
              key={`${property.id}${property.featuredListing}${property.type}`}
              href={getPropertyHref(property.path, props.currency)}
              property={property}
              onFavourite={props.onFavourite}
              onHideToggle={props.onHideToggle}
              imageLocator={props.imageLocator}
              locationDetail={props.locationDetail}
              thumbImageLocator={props.thumbImageLocator}
              loading="lazy"
              useWideDisplay
              useSlider={props.photoScroller}
              onCallClick={props.onCallClick}
              onEmailClick={props.onEmailClick}
              onClick={handlePropertyClick(property)}
            />
          ))}
        </BoxList>

        {props.footer}
      </List>

      {!!properties.data?.results && (
        <HandyTools>
          <OpenSearchMap
            importantPlaces={properties.data.importantPlaces}
            polygons={properties.data?.polygons}
            url={properties.data.url}
          />

          {platform.select(
            null,
            <FeaturedSearchAgent agent={properties.data.featuredAgent} onCallClick={props.onCallAgentClick} />,
          )}

          <DesktopOnly>
            <InfoBox>
              <InfoHeader>Handy tools</InfoHeader>

              <button onClick={props.onDrawSearchClick} type="button">
                <FontAwesomeIcon
                  icon={faPencil}
                  color={theme.textDark}
                  style={{ fontSize: 22, width: 28, marginRight: 10 }}
                />
                {properties.data?.searchForm?.phrase.drawnSearch ? 'Edit your drawn search' : 'Draw your own search'}
              </button>
              <button onClick={props.onTravelTimeClick} type="button">
                <FontAwesomeIcon
                  icon={faCarSide}
                  color={theme.textDark}
                  style={{ fontSize: 22, width: 28, marginRight: 10 }}
                />
                {properties.data?.searchForm?.phrase.travelTimeSearch
                  ? 'Update travel search'
                  : 'Add your own travel search'}
              </button>
            </InfoBox>

            <AdContainer>
              <AdBox
                id="SRPright"
                config="SRPright"
                hideUnder={1280}
                sizes={AD_RIGHT_SIZE}
                url={router.asPath}
                displayMargin={250}
                refresh
                timedRefresh
              />
            </AdContainer>

            {/* {!!properties.data?.refineLinks?.length && (
              <InfoBox>
                <InfoHeader>Refine your location</InfoHeader>

                {properties.data.refineLinks.map((link) => (
                  <Link href={link.url} prefetch={false} key={link.url} onClick={trackRefineLink('refine')}>
                    {link.label}
                  </Link>
                ))}
              </InfoBox>
            )} */}

            {!!properties.data?.nearbyLinks?.length && (
              <InfoBox>
                <InfoHeader>Nearby towns and cities</InfoHeader>

                {properties.data.nearbyLinks.map((link) => (
                  <Link href={link.url} prefetch={false} key={link.url} onClick={trackRefineLink('nearby')}>
                    {link.label}
                  </Link>
                ))}
              </InfoBox>
            )}

            {!!properties.data?.expandLinks?.length && (
              <InfoBox>
                <InfoHeader>Expand your search</InfoHeader>

                {properties.data.expandLinks.map((link) => (
                  <Link href={link.url} prefetch={false} key={link.url} onClick={trackRefineLink('expand')}>
                    {link.label}
                  </Link>
                ))}
              </InfoBox>
            )}

            {!!props.recentSearches?.length && (
              <InfoBox>
                <InfoHeader>Recent Searches</InfoHeader>

                {props.recentSearches.map((recentSearch) => (
                  <RecentSearchButton key={recentSearch.description.complete}>
                    <button onClick={() => props.onRecentSearchClick(recentSearch)} type="button">
                      <h4>{recentSearch.description.stem}</h4>

                      {!!recentSearch.description.details && <p>{recentSearch.description.details}</p>}
                    </button>

                    <button onClick={() => props.onRemoveRecentSearch(recentSearch)} type="button">
                      <FontAwesomeIcon icon={faTimes} size={20} />
                    </button>
                  </RecentSearchButton>
                ))}
              </InfoBox>
            )}

            {platform.select(null, <RecentlyViewed />)}

            {properties.data && properties.data.results && properties.data.results.length > 0 && (
              <StickyBox>
                <AdContainer>
                  <AdBox
                    id="SRPrightbottom"
                    config="SRPrightbottom"
                    hideUnder={1280}
                    sizes={AD_RIGHT_BOTTOM_SIZE}
                    containerStyle={{ top: 0 }}
                    url={router.asPath}
                    displayMargin={250}
                    collapse
                    refresh
                    timedRefresh
                  />
                </AdContainer>
              </StickyBox>
            )}
          </DesktopOnly>
        </HandyTools>
      )}
    </Container>
  );
};

export default PropertyList;
